/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-lines-per-function */
import * as Sentry from '@sentry/react';
import { captureException } from '@sentry/react';
import ArrowLeft2 from 'iconsax-react/dist/esm/ArrowLeft2';
import InfoCircle from 'iconsax-react/dist/esm/InfoCircle';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import isNumber from 'lodash/isNumber';
import toPairs from 'lodash/toPairs';
import toString from 'lodash/toString';
import { Color, CommonMpSdk, Mattertag, ShowcaseEmbedWindow, Tour } from 'matterport';
import { MpSdk } from 'matterport/dist/sdk';
import { default as React, useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { getTableDisplayName, policyConditionApplies, TDR } from 'tdr-common';
import { useSearch } from '../../api/availability/search';
import config from '../../common/config';
import { URLParams } from '../../common/types';
import { useViewToggle } from '../../context/ViewToggleProvider';
import { useMatterport } from '../../context/new/MatterportContext';
import { useTableSelectionContext } from '../../context/TableSelectionContext';
import { analytics, MP_TAG_CLICKED } from '../../helpers/analytics';
import { withTimeout } from '../../helpers/retry';
import { useScreenSize } from '../../hooks/useScreenSize';
import { useSmoothState } from '../../hooks/useSmoothState';
import available from '../../images/pins/available.svg';
import normal from '../../images/pins/default.svg';
import disabled from '../../images/pins/disabled.svg';
import limited from '../../images/pins/limited.svg';
import unavailable from '../../images/pins/unavailable.svg';
import { SearchFilters, parseSearchParamsFilters } from '../../routes/AvailabilityGuard';
import { RestaurantGuardOutletContext } from '../../routes/RestaurantGuard';
import { AnimatedImageGallery } from '../animated-image-gallery/AnimatedImageGallery';
import { CTAButton } from '../buttons';
import { MatterportEducationalOverlay } from '../matterport-educational-overlay/MatterportEducationalOverlay';
import { Skeleton } from '../skeleton/Skeleton';
import { MatterportEnvironmentNotice, MatterportEnvironmentNoticeProps } from '../table-proximity/MatterportEnvironmentNotice';
import styles from './Matterport.module.scss';
import { buildMatterportColorFilter } from './colorAdjust';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';


const PINS = {
	default: normal,
	disabled,
	available,
	limited,
	unavailable
};

const PIN_3D_OBJECTS = {
	[TDR.PinType.AVAILABLE]: 'https://cdn.tablz.net/3dPin/available_pin.glb',
	[TDR.PinType.LIMITED]: 'https://cdn.tablz.net/3dPin/limited_pin.glb',
	[TDR.PinType.UNAVAILABLE]: 'https://cdn.tablz.net/3dPin/unavailable_pin.glb?v=2',
	[TDR.PinType.DISABLED]: 'https://cdn.tablz.net/3dPin/unavailable_pin.glb?v=2',
	[TDR.PinType.DEFAULT]: 'https://cdn.tablz.net/3dPin/unavailable_pin.glb?v=2'
};
export interface MatterportLayoutProps {
	restaurantId: string;
	brightness?: number;
	contrast?: number;
	exposure?: number;
	highlights?: number;
	shadows?: number;
	temperature?: number;
	saturation?: number;
	modelId: string;
	condition: TDR.PricingPolicy.Condition
}

export const MatterportEnvironment = ({ restaurantId, brightness = 1, contrast = 1, exposure, highlights, shadows, temperature, saturation, modelId, condition }: MatterportLayoutProps) => {
	const { tables, restaurant, featureFlags } = useOutletContext<RestaurantGuardOutletContext>();

	const { isError, setIsError, isLoading, setIsLoading } = useMatterport();
	const { isSmallScreen } = useScreenSize();
	const { isVirtualView } = useViewToggle();
	const { firstAvailableTable } = useTableSelectionContext();

	const { tableSlug } = useParams<URLParams>();
	const [searchParams] = useSearchParams();

	const [sdk, setSdk] = useState<MpSdk>(null);
	const [tableMap, setTableMap] = useState<Record<string, TDR.Table>>({});
	const [searchFilters, setSearchFilters] = useState<SearchFilters>();
	const [selectedTable, setSelectedTable] = useState<TDR.Table>(null);
	const [pointer, setPointer] = useState<any>(null);
	const [sceneObject, setSceneObject] = useState<any>(null);
	const [clickEmitPath, setClickEmitPath] = useState<string>();
	const [directionVector, setDirectionVector] = useState({ x: 0, y: 0, z: 0, length: 0 });
	// Use useSmoothState to prevent 60 FPS re-renders from MatterportEnvironmentNotice updates
	const [closestVisibleTable, setClosestVisibleTable] = useSmoothState<TDR.Table | null>(null);

	const [bounceFn, setBounceFn] = useState(null);

	// If landing directly on table details page, we need to wait for matterport navigation to settle before setting up the overlay
	const [isSettled, setIsSettled] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();

	const locationRef = useRef(location);
	const tableMapRef = useRef(tableMap);
	const matterportFrame = useRef<HTMLIFrameElement>(null);
	const isMountedRef = useRef(true);
	const previousLocationRef = useRef<string | null>(null);

	useEffect(() => {
		previousLocationRef.current = locationRef.current?.pathname;
		locationRef.current = location;
	}, [location]);

	useEffect(() => {
		tableMapRef.current = tableMap;
	}, [tableMap]);

	useEffect(() => {
		if (searchParams) {
			setSearchFilters(parseSearchParamsFilters(searchParams, restaurant?.timezone));
		}
	}, [searchParams]);

	const { results } = useSearch({
		params: searchFilters,
		restaurantId: restaurantId,
		space: tables
	});

	const filterInstanceId = useId().replace(/[:]/g, '-');
	const colorFilter = useMemo(
		() =>
			buildMatterportColorFilter(
				{ brightness, contrast, exposure, highlights, shadows, temperature, saturation },
				filterInstanceId
			),
		[brightness, contrast, exposure, highlights, shadows, temperature, saturation, filterInstanceId]
	);

	// allow a forced refresh
	const [lastRefresh] = useReducer(() => {
		return new Date().getTime();
	}, new Date().getTime());

	// useEffect(() => {
	// 	if(closestVisibleTable && parrot) {
	// 		parrot.obj3D.position.set(closestVisibleTable.pos.x, closestVisibleTable.pos.y, closestVisibleTable.pos.z); //
	// 	}
	// }, [closestVisibleTable, parrot]);

	const [availableTables, setAvailableTables] = useState<TDR.Table[]>([]);
	const [hiddenTables, setHiddenTables] = useState<Set<string>>(new Set());
	const [currentPinType, setCurrentPinType] = useState<TDR.PinType>(TDR.PinType.UNAVAILABLE);
	const [pinObjects, setPinObjects] = useState<Record<TDR.PinType, any>>({} as Record<TDR.PinType, any>);
	const [current3DObject, setCurrent3DObject] = useState<any>(null);

	useEffect(() => {
		if (!sdk || isEmpty(tables) || !results?.metadata?.time || !tableMap) {
			return;
		}

		const newAvailableTables: TDR.Table[] = [];
		const newHiddenTables: Set<string> = new Set();
		const hideDateDependentPins = featureFlags?.hideDateDependentPins;

		tables.forEach((contextTable) => {
			const table = tableMap[contextTable.slug];
			const result = results.resultMap[contextTable.id];

			// Skip completely if feature flag is enabled and there are date conflicts
			if (hideDateDependentPins && (result?.['conflict']?.includes('date') || result?.['conflict']?.includes('groupSize'))) {
				// Hide the pin completely by removing it from Matterport
				if (table?.sid) {
					sdk?.Tag?.remove?.(table.sid);
				}
				// Track this table as hidden
				newHiddenTables.add(contextTable.id);
				return;
			}

			let newPinType: TDR.PinType = TDR.PinType.DISABLED;
			if (!table || !result) {
				return;
			}
			if (result['unSet']?.length === 3) {
				newPinType = TDR.PinType.DEFAULT;
			}
			else if (result['match']?.includes('groupSize')) {
				newAvailableTables.push(contextTable);
				if (result['conflict']?.length) {
					newPinType = TDR.PinType.LIMITED;
				}
				else {
					newPinType = TDR.PinType.AVAILABLE;
				}
			}

			if (table.pinType !== newPinType) {
				table.pinType = newPinType;
				return sdk?.Tag?.editIcon?.(table.sid, newPinType);
			}
		});

		setAvailableTables(newAvailableTables);
		setHiddenTables(newHiddenTables);

		// Clear closestVisibleTable if it becomes hidden
		if (closestVisibleTable && newHiddenTables.has(closestVisibleTable.id)) {
			setClosestVisibleTable(null);
		}
	}, [sdk, results?.metadata?.time, tableMap, featureFlags?.hideDateDependentPins, closestVisibleTable]);

	// sdk && sdk.App.state.waitUntil(function (state) {
	// 	return state.phase === 'appphase.playing' as CommonMpSdk.App.Phase.PLAYING;
	// });

	const gotoTable = async (sdk: MpSdk, table: TDR.Table): Promise<void | string> => {
		// match to our locally enhanced table if possible
		table = tableMap[table?.slug || table?.sid] || table;

		if (isNumber(table?.tourStop) && table?.tourStop >= 0) {
			return sdk.Tour.step(table.tourStop).catch((err) => {
				console.warn(err);
				sdk.Tour.stop();
			});
		}
		else if (table?.sid) {
			return sdk.Mattertag.navigateToTag(table.sid, sdk.Mattertag.Transition.FLY).catch((err) => console.warn(err));
		}
		else {
			return Promise.resolve();
		}
	};

	const addTable = async (
		sdk: MpSdk,
		table: TDR.Table,
		options: {
			preventPopup: boolean;
			color: Color;
		}
	): Promise<{ table: TDR.Table; newMap: any }> => {
		const displayName = getTableDisplayName(table);
		const sids: string[] = await sdk.Tag.add({
			label: displayName,
			anchorPosition: { ...table.pos },
			//disable the stem line.
			stemVector: { x: 0, y: 0, z: 0 },
			iconId: 'default'
		});

		// theoretically, I could have made a single call to `sdk.add` with ALL the tables, BUT
		// I don't know for a fact that batches the matterport IDs in the same order, so I'm going one at a time
		const [sid] = sids;
		const newMap = {};

		newMap[sid] = table;
		newMap[table.slug] = table;

		table['sid'] = sid;

		if (options.preventPopup) {
			await sdk.Tag.allowAction(sid, {
				docking: false,
				navigating: true,
				opening: false
			});
		}

		return { table, newMap };
	};

	const setTables = async (sdk: MpSdk, tables: TDR.Table[]): Promise<TDR.Table[]> => {
		try {
			const tourSnapshots: Tour.Snapshot[] = await sdk.Tour.getData();

			const result = await Promise.all(
				tables.filter(table => (modelId === table.model) && !table.disabled && !table.deleted).map((table) => {
					const stopIndex = tourSnapshots.findIndex((stop) => (stop.name === table.name) || (stop.name === table.internalName));
					return addTable(
						sdk,
						{ ...table, tourStop: stopIndex },
						{
							preventPopup: true,
							color: null
						}
					);
				})
			);

			let mergedMap = {};
			result
				.map((r) => r.newMap)
				.forEach((newMap) => {
					mergedMap = { ...mergedMap, ...newMap };
				});
			setTableMap(mergedMap);
		}
		catch (error) {
			setIsError(true);
			captureExceptionWithManualDetails(error, 'inside setTables');
		}

		return tables;
	};

	const onTagClick = (sid: string, tableMap: Record<string, TDR.Table>) => {
		if (!tableMap) {
			return;
		}
		const table = tableMap[sid];

		analytics.track(MP_TAG_CLICKED, {
			tableSlug: table.slug
		});

		navigate({
			pathname: `explore/${table.slug}`,
			search: locationRef.current.search
		});
	};

	//Used for debugging bad promises
	function captureExceptionWithManualDetails(e: Error, label: string) {
		console.error(label);
		const stackTrace = new Error().stack;
		console.error(stackTrace);
		captureException(e);
	}

	useEffect(() => {
		if (tableMap && !isNull(sdk)) {
			const handleClick = (sid: string) => onTagClick(sid, tableMapRef.current);
			sdk.on('tag.click' as Mattertag.Event.CLICK, handleClick);

			return () => {
				sdk.off('tag.click' as Mattertag.Event.CLICK, handleClick);
			};
		}
	}, [sdk]);

	useEffect(() => {
		if (!isNull(sdk) && !!tables) {
			const setup = async () => {
				// load the pin images
				try {
					await Promise.all(
						toPairs(PINS).map(([key, value]) => {
							return sdk.Asset.registerTexture(key, value);
						})
					);
				}
				catch (e) {
					if (e) {
						Sentry.captureMessage(`Unable to register all textures: ${e.message}`);
					}
				}

				await removeAllTags();
				await setTables(sdk, tables);
				const [sceneObject] = await (sdk as any).Scene.createObjects(1);

				setSceneObject(sceneObject);

				// Create 3D objects for each pin type
				const newPinObjects: Record<TDR.PinType, any> = {} as Record<TDR.PinType, any>;
				const pinTypes = Object.keys(PIN_3D_OBJECTS) as TDR.PinType[];

				for (const pinType of pinTypes) {
					try {
						const pinNode = sceneObject.addNode(`pin-node-${pinType}`);
						const gltfComponent = pinNode.addComponent('mp.gltfLoader', {
							url: PIN_3D_OBJECTS[pinType],
							localScale: {
								x: 3,
								y: 3,
								z: 3
							},
							localRotation: {
								x: 90,
								y: 90,
								z: 90
							}
						}, `pin-component-${pinType}`);

						// Position off-screen initially
						pinNode.obj3D.position.set(1000, 1000, 1000);

						newPinObjects[pinType] = {
							node: pinNode,
							component: gltfComponent
						};
					}
					catch (error) {
						console.warn(`Failed to load 3D object for pin type ${pinType}:`, error);
						Sentry.captureMessage(`Failed to load 3D object for pin type ${pinType}: ${error.message}`);
					}
				}

				setPinObjects(newPinObjects);
				setCurrent3DObject(newPinObjects[TDR.PinType.UNAVAILABLE]);
				setPointer(newPinObjects[TDR.PinType.UNAVAILABLE]?.node);

				// add another scene node to contain the light objects.
				const lightsNode = sceneObject.addNode();

				// Add directional and ambient lights
				const directionalLightComponet = lightsNode.addComponent('mp.directionalLight', {
					color: { r: 0.9, g: 0.9, b: 0.9 },
					intensity: 1.2
				});
				lightsNode.addComponent('mp.ambientLight', {
					intensity: 1.0,
					color: { r: 1.0, g: 1.0, b: 1.0 }
				});

				// Add a path id 'ambientIntensity' to the intensity property of the directional light component.
				// The path will be used to set the value later.
				const ambientIntensityPath = sceneObject.addInputPath(directionalLightComponet, 'intensity', 'ambientIntensity');

				// Start the scene object and its nodes.
				sceneObject.start();

				// Set up interaction for the initial 3D object
				if (newPinObjects[TDR.PinType.UNAVAILABLE]) {
					const clickEmitPath = sceneObject.addEmitPath(
						newPinObjects[TDR.PinType.UNAVAILABLE].component,
						'INTERACTION.CLICK'
					);
					setClickEmitPath(clickEmitPath);
					const hoverEmitPath = sceneObject.addEmitPath(
						newPinObjects[TDR.PinType.UNAVAILABLE].component,
						'INTERACTION.HOVER'
					);

					const hoverSpy = sceneObject.spyOnEvent({
						path: hoverEmitPath,
						onEvent(payload: any) {
							if (payload.hover) {
								matterportFrame.current.contentWindow['enableMatterportHoverClass']?.();
							}
							else {
								matterportFrame.current.contentWindow['disableMatterportHoverClass']?.();
							}
						}
					});
				}
			};

			setup();
		}
	}, [sdk, tables]);

	useEffect(() => {
		if (!isNull(tableSlug) && tables && tableMap) {
			const table = tables.find((table) => table.slug === tableSlug);
			if (table) {
				setSelectedTable(table);
				gotoTable(sdk, table);
			}
			else {
				setSelectedTable(null);
			}
		}
	}, [tableSlug, tables, tableMap]);

	// Auto-navigate to first available table when on explore page
	useEffect(() => {
		// Only auto-navigate if:
		// 1. We're on the explore page (no tableSlug)
		// 2. We have a first available table
		// 3. SDK is ready
		// 4. Tables and tableMap are loaded
		// 5. NOT coming from a table page (previousLocationRef doesn't include a table slug)
		// 6. NOT coming from checkout (previousLocationRef doesn't include '/checkout')
		const isComingFromTablePage = previousLocationRef.current?.includes('/explore/') &&
			previousLocationRef.current?.split('/').length > 3 &&
			!previousLocationRef.current?.includes('/filters') &&
			!previousLocationRef.current?.includes('/request');
		const isComingFromCheckout = previousLocationRef.current?.includes('/checkout');

		if (!tableSlug && firstAvailableTable && sdk && tables && tableMap &&
			!isComingFromTablePage && !isComingFromCheckout) {
			gotoTable(sdk, firstAvailableTable);
		}
	}, [tableSlug, firstAvailableTable, sdk, tables, tableMap]);

	function bouncingEffect(time: number): number {
		const frequency = 1;
		const decay = 0.3;

		const numBounces = 2;
		const bounceDuration = (numBounces * Math.PI) / frequency;

		if (time > bounceDuration) {
			return 0;
		}
		const bounce = Math.abs(Math.sin(time * frequency) * Math.exp(-decay * time));

		return Math.min(1, Math.max(0, bounce));
	}

	useEffect(() => {
		if (closestVisibleTable && pointer && !hiddenTables.has(closestVisibleTable.id)) {
			let i = 0;
			const intervalId = setInterval(() => {
				const lengthScaled = Math.pow(directionVector.length / 20, 0.5);
				pointer.obj3D.position.set(
					closestVisibleTable.pos.x,
					closestVisibleTable.pos.y + bouncingEffect(i) * lengthScaled + 0.60 * lengthScaled,
					closestVisibleTable.pos.z
				);

				i += 0.1;
				if (i > 50) {
					clearInterval(intervalId);
				}
			}, 20);

			setBounceFn(intervalId);

			return () => {
				pointer.obj3D.position.set(
					1000,
					1000,
					1000
				);
				clearInterval(intervalId);
			};
		}
		else {
			if (bounceFn) {
				clearInterval(bounceFn);
				setBounceFn(null);
			}
		}
	}, [closestVisibleTable, pointer, hiddenTables]);

	useEffect(() => {
		if (pointer && directionVector && closestVisibleTable && !hiddenTables.has(closestVisibleTable.id)) {
			pointer.obj3D.rotation.set(directionVector.x, directionVector.y, directionVector.z);
			const lengthScaled = Math.pow(directionVector.length / 2.5, 0.5);
			pointer.obj3D.scale.set(lengthScaled, lengthScaled, lengthScaled);
		}
	}, [directionVector, pointer, closestVisibleTable, hiddenTables]);

	const switch3DObject = (pinType: TDR.PinType) => {
		if (!pinObjects[pinType] || !sceneObject) {
			return;
		}

		// Hide current object
		if (current3DObject) {
			current3DObject.node.obj3D.position.set(1000, 1000, 1000);
		}

		// Show new object
		const newObject = pinObjects[pinType];
		setCurrent3DObject(newObject);
		setPointer(newObject.node);
		setCurrentPinType(pinType);

		// Update click and hover event listeners for the new object
		try {
			const newClickEmitPath = sceneObject.addEmitPath(
				newObject.component,
				'INTERACTION.CLICK'
			);
			setClickEmitPath(newClickEmitPath);

			const newHoverEmitPath = sceneObject.addEmitPath(
				newObject.component,
				'INTERACTION.HOVER'
			);

			sceneObject.spyOnEvent({
				path: newHoverEmitPath,
				onEvent(payload: any) {
					if (payload.hover) {
						matterportFrame.current?.contentWindow?.['enableMatterportHoverClass']?.();
					}
					else {
						matterportFrame.current?.contentWindow?.['disableMatterportHoverClass']?.();
					}
				}
			});
		}
		catch (error) {
			console.warn('Failed to set up 3D object interactions:', error);
		}
	};

	// Switch 3D object based on closest visible table availability
	useEffect(() => {
		if (!closestVisibleTable || !pinObjects || Object.keys(pinObjects).length === 0) {
			return;
		}

		// Skip 3D object switching for hidden tables
		if (hiddenTables.has(closestVisibleTable.id)) {
			return;
		}

		// Determine pin type based on table availability
		const table = tableMap[closestVisibleTable.slug];
		if (!table || !table.pinType) {
			return;
		}

		// Switch to appropriate 3D object if different from current
		if (table.pinType !== currentPinType) {
			switch3DObject(table.pinType);
		}
	}, [closestVisibleTable, tableMap, pinObjects, currentPinType, hiddenTables]);

	const connect = async () => {
		const mpSDK = (matterportFrame?.current?.contentWindow as ShowcaseEmbedWindow)?.MP_SDK;
		return mpSDK?.connect?.(matterportFrame.current, config.matterport.sdkKey, '');
	};

	async function removeAllTags() {
		return sdk.Mattertag.getData()
			.then((tags) => {
				return sdk.Tag.remove(...tags.map((tag) => tag.sid));
			})
			.catch((e) => {
				captureExceptionWithManualDetails(e, 'reloadTags');
			});
	}

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Move the scene object click handler useEffect to before early returns
	useEffect(() => {
		const goToClosestVisibleTable = () => {
			if (closestVisibleTable && sdk) {
				analytics.track(MP_TAG_CLICKED, {
					tableSlug: closestVisibleTable.slug,
					iconType: '3D'
				});

				navigate({
					pathname: `explore/${closestVisibleTable.slug}`,
					search: locationRef.current.search
				});
			}
		};

		if (sceneObject && clickEmitPath && closestVisibleTable) {
			const clickSpy = sceneObject.spyOnEvent({
				path: clickEmitPath,
				onEvent(payload: any) {
					goToClosestVisibleTable();
				}
			});
		}
	}, [closestVisibleTable, sceneObject, clickEmitPath, sdk, navigate, location]);

	// Disable matterport during tests
	if (config.matterport.disabled) {
		return (
			<>
				<MatterportDisabled />
			</>
		);
	}

	if (isError) {
		return isSmallScreen ? null : <AnimatedImageGallery staticImage={true} restaurant={restaurant} tables={tables} />;
	}
	const shouldDisplay = selectedTable ? selectedTable?.model === modelId : policyConditionApplies(condition, { reservation: { time: DateTime.fromISO(`${searchFilters?.date}T${searchFilters?.time ?? '00:00'}`) } });

	return (
		<span style={shouldDisplay ? {} : { display: 'none' }}>
			{isLoading && <Skeleton width='100%' height='100%' />}
			{colorFilter?.filterElement}
			<iframe
				title='Matterport restaurant 3d walkthrough'
				aria-hidden='true'
				ref={matterportFrame}
				id='mpframe'
				tabIndex={-1}
				className={styles.matterport}
				allowFullScreen
				allow='xr-spatial-tracking'
				style={{ filter: colorFilter ? `url(#${colorFilter.filterId})` : 'none', border: 0 }}
				src={`/bundle/showcase.html?${new URLSearchParams({
					m: modelId,
					hr: toString(0),
					qs: toString(1),
					title: toString(0),
					play: toString(1),
					newtags: '1',
					log: '0',
					brand: '0',
					tour: '0',
					ts: '-1',
					gt: '0',
					mls: '2',
					tagNav: '0',
					search: '0',
					tourcta: '0',
					vr: '0',
					// this is the only way to force the old tags implementation https://matterport.github.io/showcase-sdk/earlyaccess_home.html#get-the-early-access-bundle-sdk
					applicationKey: config.matterport.sdkKey,
					last_refresh: toString(lastRefresh) // change src with a harmless param to force a reload
				}).toString()}`}
				onError={() => {
					setIsError(true);
					setIsLoading(false);
				}}
				onLoad={async () => {
					try {
						const sdk: CommonMpSdk = await withTimeout(10000, connect());

						if (sdk) {
							sdk.App.state.subscribe((appState) => {
								if (appState.phase === sdk.App.Phase.PLAYING) {
									// Wait for camera to settle before showing overlay
									setTimeout(() => {
										setIsSettled(true);
									}, 6000);
									setTimeout(() => {
										sdk.Camera.zoomBy(-1);
									}, 100);
								}
							});

							if (isMountedRef.current) {
								setSdk(sdk);
							}
						}
						else {
							throw new Error(`Unable to load the matterport SDK, the connect call returned: ${sdk}`);
						}
					}
					catch (e) {
						setIsError(true);
						if (!(e && typeof e === 'string' && e.includes('Timed out'))) {
							const eventID = captureException(e);
							analytics.track('Matterport SDK Loading Failed', {
								sentryErrorID: eventID
							});
						}
						else {
							console.warn('Matterport SDK timeout');
							analytics.track('Matterport SDK Loading Failed', {
								message: e
							});
						}
					}
					finally {
						setIsLoading(false);
					}
				}}
			/>

			{isVirtualView || isSmallScreen ? <BackButton /> : null}

			<MatterportEnvironmentNotice sdk={sdk} tables={tables} availableTables={availableTables} hiddenTables={hiddenTables} gotoTable={gotoTable} closestVisibleTable={closestVisibleTable} setClosestVisibleTable={setClosestVisibleTable} setDirectionVector={setDirectionVector} />

			{!isSmallScreen && isSettled && <MatterportEducationalOverlay sdk={sdk} restaurantName={restaurant.displayName || restaurant.name} />}
		</span>
	);
};

const MatterportDisabled = () => {
	return (
		<p className={styles['matterport-disabled-msg']}>
			<InfoCircle />
			Matterport iframe disabled for testing
		</p>
	);
};

const BackButton = () => {
	const { turnOffVirtualView } = useViewToggle();
	const { t } = useTranslation();

	return (
		<div className={styles.BackButton}>
			<CTAButton variant='transparent' buttonText={t('actions.back')} iconBefore={<ArrowLeft2 />} onClick={turnOffVirtualView} />
		</div>
	);
};
